import { useEffect, useRef, useState } from 'react';
import type { GameSocketApi } from '../hooks/useGameSocket';
import { useGameStore, useMyId, useAmDrawer, useCanGuess } from '../store/gameStore';

export default function Chat({ socket }: { socket: GameSocketApi }) {
  const chat = useGameStore((s) => s.chat);
  const myId = useMyId();
  const isDrawer = useAmDrawer();
  const canGuess = useCanGuess();
  const [text, setText] = useState('');
  const feedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight, behavior: 'smooth' });
  }, [chat]);

  const send = (e: React.FormEvent) => {
    e.preventDefault();
    const t = text.trim().slice(0, 200);
    if (!t) return;
    socket.sendChat(t);
    setText('');
  };

  return (
    <div className="flex h-full min-h-0 flex-col rounded-2xl border border-scr-border bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-scr-border px-4 py-2.5">
        <span className="text-xs font-bold uppercase tracking-widest text-scr-ink/50">
          {isDrawer ? 'Spectators' : 'Guesses'}
        </span>
        {isDrawer && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-600">
            you're drawing
          </span>
        )}
      </div>

      {/* Feed */}
      <div
        ref={feedRef}
        className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-3"
      >
        {chat.map((c) => {
          const mine = c.authorId === myId;
          const system = c.authorId === null;
          const correct = c.kind === 1;
          return (
            <div key={c.id} className="leading-snug">
              {system ? (
                <p className="text-center text-[11px] font-medium italic text-scr-ink/45">
                  {c.text}
                </p>
              ) : (
                <p className="text-sm">
                  <span
                    className={`font-bold ${correct ? 'text-scr-accent-dark' : mine ? 'text-scr-ink' : 'text-scr-ink/60'}`}
                  >
                    {c.authorName}:
                  </span>{' '}
                  <span className={correct ? 'font-semibold text-scr-accent-dark' : 'text-scr-ink/75'}>
                    {c.text}
                  </span>
                </p>
              )}
            </div>
          );
        })}
        {chat.length === 0 && (
          <p className="pt-6 text-center text-xs text-scr-ink/35">
            {isDrawer ? 'No one chatting yet — start sketching!' : 'Guess the word as it gets drawn!'}
          </p>
        )}
      </div>

      {/* Input */}
      <form onSubmit={send} className="flex gap-2 border-t border-scr-border p-2.5">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={isDrawer}
          maxLength={200}
          placeholder={isDrawer ? 'Watch the guesses roll in…' : 'Type your guess…'}
          className="min-w-0 flex-1 rounded-xl border border-scr-border bg-scr-bg px-3 py-2 text-sm outline-none transition focus:border-scr-accent disabled:cursor-not-allowed disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={isDrawer || !text.trim()}
          className="rounded-xl bg-scr-accent px-4 py-2 text-sm font-bold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Send
        </button>
      </form>
      {isDrawer && (
        <p className="px-3 pb-2 text-center text-[10px] text-scr-ink/40">
          The drawer can chat, but guesses coming from you don't count.
        </p>
      )}
    </div>
  );
}